import React from 'react';
import {
  BookOpenCheck,
  Clock3,
  Headphones,
  Sparkles,
  Star,
  Users,
} from 'lucide-react';
import { BRAND_NAME } from '../../config/brand';
import ScrollReveal from '../common/ScrollReveal';

const toneClass = {
  navy: {
    card: 'border-[#E5DCCF] bg-[#FAF7F0]',
    icon: 'bg-white/90 text-[#001848] shadow-sm',
    blob: 'bg-[#001848]/10',
  },
  gold: {
    card: 'border-[#E0C99A]/70 bg-[#F8F1E4]/90',
    icon: 'bg-white/90 text-[#A87824] shadow-sm',
    blob: 'bg-[#C09048]/15',
  },
  soft: {
    card: 'border-[#E5DCCF] bg-[#EFE8DC]/70',
    icon: 'bg-white/90 text-[#001848] shadow-sm',
    blob: 'bg-[#001848]/08',
  },
} as const;

const HOME_ITEMS = [
  {
    src: '/img/home-items/1.svg',
    title: 'منهج شامل',
    description: 'باقات مميزة للمراجعات بتساعدك تتقن المنهج في مدة قصيرة',
    Icon: BookOpenCheck,
    tone: 'navy' as const,
  },
  {
    src: '/img/home-items/2.svg',
    title: 'جودة عالية',
    description: 'محتوى تعليمي بجودة عالية تليق بتميز الطلاب',
    Icon: Star,
    tone: 'gold' as const,
  },
  {
    src: '/img/home-items/3.svg',
    title: 'مدرسين خبراء',
    description: 'مدرسين بخبرة طويلة في تدريس كل المراحل وكل المواد',
    Icon: Users,
    tone: 'soft' as const,
  },
  {
    src: '/img/home-items/4.svg',
    title: 'تعلم في أي وقت',
    description: 'شوف الدروس وقت ما تحب، من موبايلك أو لابتوبك في أي مكان',
    Icon: Clock3,
    tone: 'navy' as const,
  },
  {
    src: '/img/home-items/5.svg',
    title: 'مراجعات وامتحانات',
    description: 'أسئلة ومراجعات شاملة تجهزك للامتحانات بثقة',
    Icon: Sparkles,
    tone: 'gold' as const,
  },
  {
    src: '/img/home-items/6.svg',
    title: 'دعم مستمر',
    description: 'فريق دعم ومجتمع طلابي يساعدك طول رحلتك التعليمية',
    Icon: Headphones,
    tone: 'soft' as const,
  },
] as const;

const WhyChooseBanner: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const content = (
    <>
      <ScrollReveal variant="up" className="mb-8 text-center sm:mb-10 lg:mb-12">
        <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#E0C99A]/80 bg-[#F8F1E4]/90 px-3.5 py-1.5 text-xs font-semibold text-[#A87824] shadow-sm sm:text-sm">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          لماذا نحن؟
        </span>
        <h2 className="mb-3 text-2xl font-bold leading-tight text-[#001848] font-cairo-bold sm:text-3xl lg:text-4xl">
          ليه {BRAND_NAME} أفضل اختيار ليك؟
        </h2>
        <p className="mx-auto max-w-2xl text-sm leading-relaxed text-[#5A6B82] sm:text-base">
          منصة متكاملة بتغطي كل المراحل الدراسية وكل المواد، من الشرح للمراجعة للدعم
        </p>
      </ScrollReveal>

      <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:gap-4 lg:grid-cols-3 lg:gap-5">
        {HOME_ITEMS.map((item, index) => {
          const Icon = item.Icon;
          const tone = toneClass[item.tone];
          return (
            <ScrollReveal key={item.title} variant="up" delay={index * 80}>
              <article
                className={`group relative h-full overflow-hidden rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-[#001848]/10 sm:rounded-3xl sm:p-5 lg:p-6 ${tone.card}`}
              >
                <div
                  aria-hidden
                  className={`pointer-events-none absolute -top-8 -end-8 h-28 w-28 rounded-full blur-2xl transition-opacity duration-300 group-hover:opacity-90 ${tone.blob}`}
                />

                <div className="relative flex h-full flex-col">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div
                      className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl sm:h-14 sm:w-14 ${tone.icon}`}
                    >
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
                    </div>
                    <img
                      src={item.src}
                      alt=""
                      aria-hidden
                      className="h-14 w-auto max-w-[4.5rem] object-contain opacity-90 transition-transform duration-500 group-hover:scale-105 sm:h-16 sm:max-w-[5rem]"
                      loading="lazy"
                    />
                  </div>

                  <h3 className="mb-1.5 text-base font-bold leading-snug text-[#001848] font-cairo-bold sm:mb-2 sm:text-lg">
                    {item.title}
                  </h3>
                  <p className="text-xs leading-relaxed text-[#5A6B82] sm:text-sm">{item.description}</p>
                </div>
              </article>
            </ScrollReveal>
          );
        })}
      </div>
    </>
  );

  if (embedded) {
    return <div id="why-choose-section">{content}</div>;
  }

  return (
    <section id="why-choose-section" className="relative overflow-hidden py-12 sm:py-14 lg:py-16">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{content}</div>
    </section>
  );
};

export default WhyChooseBanner;
